import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Lightbulb,
  Users,
  TrendingUp,
  ChevronLeft,
  ChevronRight,
  Quote
} from 'lucide-react';
import logo from '@/assets/Decixa_logo.png';

const features = [
  {
    icon: Lightbulb,
    title: 'AI-Powered Insights',
    description: 'Get intelligent analysis for better decisions',
  },
  {
    icon: Users,
    title: 'Community Wisdom',
    description: 'Tap into collective intelligence',
  },
  {
    icon: TrendingUp,
    title: 'Track Your Impact',
    description: 'See how your decisions help others',
  },
];

const stats = [
  { value: '50K+', label: 'Decisions Made' },
  { value: '12K+', label: 'Active Users' },
  { value: '95%', label: 'Satisfaction' },
];

const testimonials = [
  {
    id: 1,
    quote: "Decixa helped me make a career decision I'd been struggling with for months. The community feedback was invaluable.",
    author: "Sarah Chen",
    role: "Product Manager",
    company: "TechCorp",
    avatar: "SC",
    gradientFrom: "from-purple-400",
    gradientTo: "to-pink-400",
  },
  {
    id: 2,
    quote: "The AI insights gave me perspectives I hadn't considered. It's like having a team of advisors in my pocket.",
    author: "Marcus Johnson",
    role: "Startup Founder",
    company: "InnovateLabs",
    avatar: "MJ",
    gradientFrom: "from-blue-400",
    gradientTo: "to-cyan-400",
  },
  {
    id: 3,
    quote: "Our team uses Decixa for major business decisions. The structured approach has improved our decision quality significantly.",
    author: "Emily Rodriguez",
    role: "VP of Operations",
    company: "ScaleUp Inc",
    avatar: "ER",
    gradientFrom: "from-emerald-400",
    gradientTo: "to-teal-400",
  },
  {
    id: 4,
    quote: "Finally, a tool that doesn't just give answers but helps me think through the decision myself. Game changer!",
    author: "David Park",
    role: "Senior Engineer",
    company: "CloudBase",
    avatar: "DP",
    gradientFrom: "from-orange-400",
    gradientTo: "to-amber-400",
  },
  {
    id: 5,
    quote: "The polling feature is perfect for getting quick feedback from my network. Love how intuitive it is.",
    author: "Aisha Patel",
    role: "Marketing Director",
    company: "BrandForward",
    avatar: "AP",
    gradientFrom: "from-rose-400",
    gradientTo: "to-pink-400",
  },
];

export default function LeftPanel() {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlaying]);

  const goToNext = () => {
    setIsAutoPlaying(false);
    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
  };

  const goToPrev = () => {
    setIsAutoPlaying(false);
    setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const goToSlide = (index: number) => {
    setIsAutoPlaying(false);
    setCurrentTestimonial(index);
  };

  return (
    <div className="w-full h-full bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 relative overflow-hidden">
      {/* Background Effects - Contained within panel */}
      <div className="absolute inset-0 pointer-events-none">
        <motion.div
          className="absolute top-20 left-10 w-72 h-72 bg-purple-500/30 rounded-full blur-3xl"
          animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute bottom-20 right-10 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl"
          animate={{ scale: [1.2, 1, 1.2], opacity: [0.2, 0.4, 0.2] }}
          transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
        />

        {/* Grid Pattern */}
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
                              linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
            backgroundSize: '50px 50px',
          }}
        />

        {/* Floating Particles */}
        {[...Array(15)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-white/40 rounded-full"
            style={{
              left: `${10 + Math.random() * 80}%`,
              top: `${10 + Math.random() * 80}%`,
            }}
            animate={{ y: [0, -20, 0], opacity: [0.2, 0.6, 0.2] }}
            transition={{
              duration: 3 + Math.random() * 3,
              repeat: Infinity,
              delay: Math.random() * 2,
            }}
          />
        ))}
      </div>

      {/* Content */}
      <div className="relative z-10 h-full flex flex-col justify-between p-8 lg:p-10 xl:p-12">
        {/* Top: Logo & Headline */}
        <div>
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-3 mb-10"
          >
            {/* <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center shadow-lg">
              <span className="text-white font-bold text-lg">Ddfs</span>
            </div> */}
            <img src={logo} alt="Decixa" className="h-18 py-2" />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <h1 className="text-2xl lg:text-3xl xl:text-4xl font-bold text-white leading-tight mb-3">
              Make decisions with
              <span className="block bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 bg-clip-text text-transparent">
                confidence & clarity
              </span>
            </h1>
            <p className="text-slate-300 text-base lg:text-lg max-w-md">
              Join thousands who trust Decixa for personal and business decisions.
            </p>
          </motion.div>
        </div>

        {/* Middle: Features */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="space-y-3"
        >
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5 + index * 0.1 }}
              className="flex items-start gap-3 p-3 rounded-xl bg-white/5 backdrop-blur-sm border border-white/10 hover:bg-white/10 transition-colors"
            >
              <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-purple-500/20 to-blue-500/20 flex items-center justify-center flex-shrink-0">
                <feature.icon className="w-4 h-4 text-purple-300" />
              </div>
              <div>
                <h3 className="text-white font-semibold text-sm">{feature.title}</h3>
                <p className="text-slate-400 text-xs">{feature.description}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="grid grid-cols-3 gap-3"
        >
          {stats.map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.9 + index * 0.1 }}
              className="text-center"
            >
              <div className="text-xl lg:text-2xl font-bold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
                {stat.value}
              </div>
              <div className="text-slate-400 text-xs">{stat.label}</div>
            </motion.div>
          ))}
        </motion.div>

        {/* Testimonials Carousel */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2 }}
          className="relative"
        >
          <div
            className="relative p-4 rounded-xl bg-white/5 backdrop-blur-sm border border-white/10"
            onMouseEnter={() => setIsAutoPlaying(false)}
            onMouseLeave={() => setIsAutoPlaying(true)}
          >
            {/* Quote Icon */}
            <div className="absolute top-3 right-3 opacity-20">
              <Quote className="w-6 h-6 text-purple-300" />
            </div>

            <AnimatePresence mode="wait">
              <motion.div
                key={currentTestimonial}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                {/* Stars */}
                <div className="flex items-center gap-0.5 mb-2">
                  {[...Array(5)].map((_, i) => (
                    <svg key={i} className="w-3.5 h-3.5 text-yellow-400 fill-current" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>

                {/* Quote */}
                <p className="text-slate-300 text-sm italic mb-3 min-h-[40px] line-clamp-2">
                  "{testimonials[currentTestimonial]?.quote}"
                </p>

                {/* Author */}
                <div className="flex items-center gap-2">
                  <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${testimonials[currentTestimonial]?.gradientFrom} ${testimonials[currentTestimonial]?.gradientTo} flex items-center justify-center text-white font-semibold text-xs`}>
                    {testimonials[currentTestimonial]?.avatar}
                  </div>
                  <div>
                    <div className="text-white text-xs font-medium">
                      {testimonials[currentTestimonial]?.author}
                    </div>
                    <div className="text-slate-500 text-xs">
                      {testimonials[currentTestimonial]?.role} • {testimonials[currentTestimonial]?.company}
                    </div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Navigation */}
            <div className="absolute top-1/2 -translate-y-1/2 left-1 right-1 flex justify-between pointer-events-none">
              <button
                onClick={goToPrev}
                className="w-6 h-6 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white/70 hover:text-white transition-colors pointer-events-auto"
              >
                <ChevronLeft className="w-3 h-3" />
              </button>
              <button
                onClick={goToNext}
                className="w-6 h-6 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white/70 hover:text-white transition-colors pointer-events-auto"
              >
                <ChevronRight className="w-3 h-3" />
              </button>
            </div>
          </div>

          {/* Dot Indicators */}
          <div className="flex items-center justify-center gap-1.5 mt-3">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`transition-all duration-300 rounded-full ${index === currentTestimonial
                  ? 'w-5 h-1.5 bg-purple-400'
                  : 'w-1.5 h-1.5 bg-white/30 hover:bg-white/50'
                  }`}
              />
            ))}
          </div>

          {/* Progress bar */}
          {isAutoPlaying && (
            <motion.div
              className="absolute -bottom-1 left-0 right-0 h-0.5 bg-purple-400/30 rounded-full overflow-hidden"
            >
              <motion.div
                className="h-full bg-purple-400"
                initial={{ width: '0%' }}
                animate={{ width: '100%' }}
                transition={{ duration: 5, ease: 'linear' }}
                key={currentTestimonial}
              />
            </motion.div>
          )}
        </motion.div>
      </div>
    </div>
  );
}